// Saved tabs. The open tabs are written to localStorage as you browse; at the next launch they
// are OFFERED back ("Restore 4 tabs") rather than reopened behind your back, unless Re:Vault
// itself reloaded the page (an update or a self-repair), when they come back as before. Restore
// is lazy: only the tab you were on loads now, the rest load when you pick them. Nothing here
// leaves the device, and tabs opened in incognito are never written down.
import { state, blankTab } from "./state.js";
import { navigateTab } from "./navigation.js";
import { openFolder } from "./folders.js";
import { render } from "./view.js";
import { setAddr } from "./dom.js";
import { get, subscribe } from "../shared/settings-store.js";
import { SESSION_KEY as KEY, SESSION_OFFER_KEY as OFFER_KEY, AUTO_RESTORE_KEY as AUTO_KEY } from "../shared/settings-schema.js";
import { snapTabs, parseSnap, decide, offerLine } from "../shared/session-snap.js";

let timer = null;
let offer = null; // the session waiting for "Restore" or "Dismiss"
const watchers = new Set();
const enabled = () => get("sessionRestore") !== false;
const lsDel = (k) => { try { localStorage.removeItem(k); } catch (e) {} };

export function saveSession() {
	clearTimeout(timer);
	timer = setTimeout(() => {
		try {
			if (!enabled() || get("incognito")) { localStorage.removeItem(KEY); return; }
			const snap = snapTabs(state.tabs, state.activeId, Date.now());
			if (snap) localStorage.setItem(KEY, JSON.stringify(snap));
			else localStorage.removeItem(KEY);
		} catch (e) {}
	}, 600);
}

export function clearSession() { lsDel(KEY); lsDel(OFFER_KEY); setOffer(null); }

// Apply a tab's pending restore target. Only ever called when the tab is active
// (on boot for the active tab, or via selectTab for the rest).
export function applyRestore(t) {
	if (!t || !t.restore) return;
	const r = t.restore; t.restore = null;
	if (r.kind === "url") navigateTab(t.id, r.target);
	else if (r.kind === "internal") {
		// An "internal" tab is a FIRST-PARTY page (settings/tools/game). Only ever
		// restore a same-origin path ("/…", not "//host"); a crafted session storing
		// an external or scheme URL here would otherwise load un-proxied as a raw
		// iframe src, bypassing cloaking and leaking the user's IP to the target.
		const ok = typeof r.target === "string" && r.target.charAt(0) === "/" && r.target.charAt(1) !== "/";
		if (!ok) { render(); return; }
		t.internal = r.target; t.view = null; t.src = ""; t.loading = true; t.skipLoader = false; setAddr(""); render();
	}
	else if (r.kind === "view") { state.activeId = t.id; openFolder(r.target, { focus: false }); }
	else render();
}

// Blank start tabs make way for the restored ones; anything you already opened stays, first.
function rebuild(snap) {
	const keep = state.tabs.filter((t) => t.url || t.internal || t.view || t.src || t.pinned);
	state.tabs = keep;
	let first = null;
	snap.tabs.forEach((s, i) => {
		const t = blankTab(state.next++);
		t.title = s.title || "New Tab";
		if (s.zoom) t.zoom = s.zoom;
		t.restore = { kind: s.kind, target: s.target };
		if (s.kind === "url") { t.url = s.target; t.addr = s.target; }
		state.tabs.push(t);
		if (i === snap.active) first = t;
	});
	state.activeId = first.id;
	applyRestore(first); // the tab you were on loads now; the rest stay asleep
}

function readOffer() {
	try { return parseSnap(localStorage.getItem(OFFER_KEY)); } catch (e) { return null; }
}
function consumeAuto() {
	try { const v = sessionStorage.getItem(AUTO_KEY); sessionStorage.removeItem(AUTO_KEY); return v === "1"; } catch (e) { return false; }
}
function setOffer(o) {
	offer = o;
	watchers.forEach((fn) => { try { fn(); } catch (e) {} });
}

// At launch, before anything renders. Returns true when tabs were rebuilt.
export function restoreSession() {
	let saved = null;
	try { saved = parseSnap(localStorage.getItem(KEY)); } catch (e) {}
	const d = decide({ saved, offer: readOffer(), auto: consumeAuto(), enabled: enabled(), incognito: !!get("incognito"), now: Date.now() });
	if (d.clear) { lsDel(KEY); lsDel(OFFER_KEY); return false; }
	if (d.restore) { rebuild(d.restore); return true; }
	// The saved session becomes the offer, so the new session can be written over KEY while the
	// offer waits, and a second crash before you answer doesn't lose it.
	if (d.keep) { try { localStorage.setItem(OFFER_KEY, JSON.stringify(d.keep)); } catch (e) {} }
	else lsDel(OFFER_KEY);
	if (d.offer) offer = d.offer;
	return false;
}

// Re:Vault's own reloads (update.js, recovery.js, a restored backup) set __rvUpdating first;
// those bring the tabs straight back instead of asking.
addEventListener("pagehide", () => {
	if (window.__rvUpdating) { try { sessionStorage.setItem(AUTO_KEY, "1"); } catch (e) {} }
});
// Answered in another window, or cleared from Settings > Privacy.
addEventListener("storage", (e) => { if (e.key === OFFER_KEY && !e.newValue && offer) setOffer(null); });
subscribe(() => { if (offer && (!enabled() || get("incognito"))) { if (!enabled()) lsDel(OFFER_KEY); setOffer(null); } });

// --- the offer, for the start page and the island ------------------------------------------------
export const restoreOffer = {
	get: () => (offer ? { count: offer.tabs.length, line: offerLine(offer) } : null),
	accept() {
		const o = offer;
		if (!o) return;
		lsDel(OFFER_KEY);
		setOffer(null);
		rebuild(o);
	},
	dismiss() { lsDel(OFFER_KEY); setOffer(null); },
	watch(fn) { watchers.add(fn); return () => watchers.delete(fn); },
};
